// src/components/Procurement/LandedCostEntryPanel.jsx
// Inline landed cost entry panel for ProcurementPage (Prompt 60)

import { formatDate } from '../../utils/format';
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { resolveSupplierPayable, pickableAccounts, scopedPartnerOptions } from '../../lib/resolveSupplierAccount';
import { AccountPick } from '../FormPickers';
import { useTranslation } from 'react-i18next';
import { Field, CheckField } from '../form/FormField';
import useActionLabel from '../../i18n/useActionLabel';
import { lineText } from '../../shell/ActionIcons';
import { emoText } from '../../shell/EmojiIcons';
import './landedCost.css';
import EnumSelect from '../EnumSelect';
import { AmountInput, TextInput } from '../inputs/Input';
import ReferenceField from '../inputs/ReferenceField';

const todayStr = () => new Date().toISOString().slice(0, 10);

const Seg = ({ value, opts }) => (
  <div className="seg" role="group">
    {opts.map(o => (
      <button key={o.k} type="button" data-testid={o.id} className={value === o.k ? 'on' : ''} aria-pressed={value === o.k}
        disabled={o.disabled} title={o.title} onClick={o.onClick}>{o.label}</button>
    ))}
  </div>
);

export default function LandedCostEntryPanel({
  open,
  onClose,
  onSuccess,
  gitItems = [],
  preselectedOperationId = null,
  embedded = false,   // rendered inside the unified Data Entry panel → drop card chrome + title
  mixedUnitOverride = null,   // authoritative mixed-unit flag from the caller (order page); overrides self-detection
}) {
  const { t } = useTranslation('landed'); const al = useActionLabel();
  const [operations, setOperations] = useState([]);
  // Engine orders (keyed by op) — the canonical FULL line composition (with units) for the selected
  // order, so mixed-unit detection sees ALL lines, not just the open goods_in_transit ones.
  const [engineByOp, setEngineByOp] = useState(new Map());
  const [selectedOpId, setSelectedOpId] = useState(preselectedOperationId || '');
  const [direction, setDirection] = useState('cost');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('by_value');   // By Value preselected (safe for any order; By Quantity stays greyed on mixed-unit)
  const [lcPayment, setLcPayment] = useState('Cash');
  const [lcAccountFrom, setLcAccountFrom] = useState('');
  const [lcPaymentDays, setLcPaymentDays] = useState('');
  const [lcDate, setLcDate] = useState(todayStr());
  const [lcNotes, setLcNotes] = useState(''); const [lcReference, setLcReference] = useState('');   // W13 (E9): the LC's OWN optional reference (set at creation only)
  const [accounts, setAccounts] = useState([]);
  const [partners, setPartners] = useState([]);      // Prompt 200: LC service-provider suppliers
  const [lcSupplierId, setLcSupplierId] = useState(''); // selected LC supplier (partner)
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingOps, setLoadingOps] = useState(false);
  // 115 (B7): D2 late-LC catch-up. When the op already has received qty, POST /landed-costs/preview
  // and render Pool vs Future from the payload (flags READ, not recomputed).
  const [catchupPreview, setCatchupPreview] = useState(null); // preview payload once fetched
  const [catchupChoice, setCatchupChoice] = useState(null);   // 'pool' | 'future'
  const [poolAck, setPoolAck] = useState(false);              // mandatory checkbox (both-impossible)
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch operations and accounts when panel opens
  useEffect(() => {
    if (!open) return;
    setLoadingOps(true);
    api.getPartners().then(ps => setPartners((ps || []).filter(p => p.type === 'Supplier'))).catch(() => {});
    Promise.all([
      api.getPlannedTransactions({}),
      api.getAccounts(),
      api.getProcurementOrders({ include_received: true }).catch(() => null),
    ]).then(([planned, accs, engine]) => {
      const git = planned?.goods_in_transit || [];
      const gitMap = {};
      for (const r of git) {
        if (!gitMap[r.operation_id]) {
          gitMap[r.operation_id] = {
            operation_id: r.operation_id,
            type: 'purchase',
            date: r.date,
            partnerName: r.partner_name,
            lines: [],
          };
        }
        gitMap[r.operation_id].lines.push(r);
      }
      setOperations(Object.values(gitMap));
      setAccounts(accs || []);
      const em = new Map();
      for (const o of (engine?.orders || [])) if (o.operation_id) em.set(o.operation_id, o);
      setEngineByOp(em);
    }).catch(() => {}).finally(() => setLoadingOps(false));
  }, [open]);

  // Prompt 199: "by quantity" is valid only when the order's lines share ONE unit. Compute distinct
  // units of the selected order; when mixed, disable that option (greyed + tooltip) and auto-switch
  // to "by value". The backend also 400s mixed-unit by-quantity as a fallback guard.
  const selectedOp = operations.find(o => o.operation_id === selectedOpId);
  // Distinct units of the WHOLE order: prefer the engine goods_lines (all lines incl. already-received
  // ones — the goods_in_transit lines only cover open lines, which misses a partially-received line's
  // unit and lets mixed-unit orders slip through). Fall back to the GIT lines when the engine is absent.
  const engLines = engineByOp.get(selectedOpId)?.goods_lines;
  const unitSource = (engLines && engLines.length) ? engLines : (selectedOp?.lines || []);
  const lcUnits = [...new Set(unitSource.map(l => (l.unit || '').trim()).filter(Boolean))];
  // Prefer the caller's authoritative flag (order page knows the units reliably); fall back to self-detection.
  const byQtyDisabled = mixedUnitOverride != null ? mixedUnitOverride : (lcUnits.length > 1);
  useEffect(() => {
    if (byQtyDisabled && method === 'by_quantity') setMethod('by_value');
  }, [byQtyDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // The LC provider (partner) is chosen first; method (Cash/Credit) is INDEPENDENT (a supplier is
  // compatible with cash — you can pay the freight company in cash). On Credit, auto-fill + grey the
  // partner's own payable account (shared resolver) and fill payment_days; on Cash a cash/bank account
  // is selectable. Clearing the partner leaves the account manual.
  const selectedLcSupplier = partners.find(p => p.id === lcSupplierId) || null;
  const { accountId: lcResolvedAcct, locked: lcAcctLocked } = resolveSupplierPayable(selectedLcSupplier, accounts);
  const lcCreditLocked = lcPayment === 'Credit' && lcAcctLocked;
  useEffect(() => {
    if (lcPayment !== 'Credit' || !lcSupplierId) return;
    if (lcResolvedAcct) setLcAccountFrom(lcResolvedAcct);
    if (selectedLcSupplier?.payment_days != null) setLcPaymentDays(String(selectedLcSupplier.payment_days));
  }, [lcPayment, lcSupplierId, lcResolvedAcct]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form when panel opens
  useEffect(() => {
    if (open) {
      setSelectedOpId(preselectedOperationId || '');
      setDirection('cost');
      setAmount('');
      setMethod('by_value');
      setLcPayment('Cash');
      setLcAccountFrom('');
      setLcPaymentDays('');
      setLcDate(todayStr());
      setLcNotes(''); setLcReference('');
      setError(null);
      setCatchupPreview(null);
      setCatchupChoice(null);
      setPoolAck(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Any change to the inputs that feed the catch-up preview invalidates it → user must re-preview.
  useEffect(() => {
    setCatchupPreview(null); setCatchupChoice(null); setPoolAck(false);
  }, [selectedOpId, amount, method, lcDate, direction]);

  if (!open) return null;

  // 114 (Model A Phase 2a): the old per-line unit_price FOLD preview was REMOVED.
  // Under Model A a landed cost never folds into unit_price — it posts to the LC Holding
  // account and is allocated to inventory at receipt. The receipt-time allocation preview
  // is Phase 2b (POST /api/landed-costs/preview); no client-side computation here.

  // Account lists (565: typed + holding-role + group excluded — drops the refund receivable from payables).
  const cashBankAccounts = pickableAccounts(accounts, ['cash', 'bank']);
  const supplierAccounts = pickableAccounts(accounts, ['supplier']);
  // 565: account→supplier scope (pool/specific/multi/orphaned). LandedCost is partner-FIRST (the account is derived
  // from the chosen supplier), so no account→partner auto-select hook here — just the scoped option list + message.
  const lcSelPayAcct = accounts.find(a => a.id === lcAccountFrom) || null;
  const { options: lcSupplierOptions, scope: lcSupplierScope } = scopedPartnerOptions(lcSelPayAcct, partners, partners);

  function validateInputs() {
    if (!selectedOpId) { setError(t('errOp')); return false; }
    if (!(parseFloat(amount) > 0)) { setError(t('errAmt')); return false; }
    if (!lcSupplierId) { setError(t('errSupp')); return false; }
    if (!lcAccountFrom) { setError(t('errAcct')); return false; }
    if (lcPayment === 'Credit' && !lcPaymentDays) { setError(t('errDays')); return false; }
    return true;
  }

  // 115 (B7): Step 1 — on submit, first ask the backend whether this LC needs a D2 catch-up choice.
  // If the op has NO received qty (catchup_applies=false), post straight through. Otherwise render the
  // Pool/Future dual-option UI from the preview payload and require a choice before the real post.
  async function handleSubmit() {
    setError(null);
    if (!validateInputs()) return;

    setLoadingPreview(true);
    try {
      const pv = await api.request('/landed-costs/preview', {
        method: 'POST',
        body: JSON.stringify({ operation_id: selectedOpId, amount: parseFloat(amount), allocation_method: method, date: lcDate }),
      });
      if (!pv?.catchup_applies) {
        // no already-received qty → no catch-up choice required
        return postLandedCost(null);
      }
      setCatchupPreview(pv);
      // Auto-select the only feasible option; leave both-available for the user to pick.
      if (pv.fully_received) setCatchupChoice('pool');           // no future qty → pool only
      else if (!pv.pool_allowed) setCatchupChoice('future');     // stock consumed → future only
      else setCatchupChoice(null);
    } catch (err) {
      setError(err.message || t('errPreview'));
    } finally {
      setLoadingPreview(false);
    }
  }

  // Step 2 — the real post (with catchup_choice when the D2 preview required one).
  async function postLandedCost(catchupChoiceVal) {
    setError(null);
    if (!validateInputs()) return;
    setSubmitting(true);
    try {
      const body = {
        operation_id: selectedOpId,
        direction: direction === 'cost' ? 'cost' : 'revenue',
        amount: parseFloat(amount),
        allocation_method: method,
        account_from_id: lcAccountFrom,
        partner_id: lcSupplierId || null,   // Prompt 200 FIX 2 / 197: stamp the LC supplier (closes lc_supplier_id NULL gap)
        payment_days: lcPayment === 'Credit' ? (parseInt(lcPaymentDays) || null) : null,
        date: lcDate,
        notes: lcNotes || null,
        ...(lcReference.trim() ? { reference_id: lcReference.trim() } : {}),
      };
      if (catchupChoiceVal) body.catchup_choice = catchupChoiceVal;
      await api.request('/landed-costs', { method: 'POST', body: JSON.stringify(body) });
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      setError(err.message || t('errPost'));
    } finally {
      setSubmitting(false);
    }
  }

  // Both-impossible fallback: fully_received AND pool blocked → holding must not strand, so Pool
  // executes regardless (backend fallback). The checkbox is UX honesty (disabled until acknowledged).
  const bothImpossible = !!(catchupPreview && catchupPreview.fully_received && !catchupPreview.pool_allowed);
  const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 }));

  const submitDisabled = !catchupChoice || (bothImpossible && !poolAck) || submitting;
  const confirmLbl = catchupChoice === 'pool' ? t('confirmPool') : catchupChoice === 'future' ? t('confirmFuture') : t('confirm');
  const unitsTxt = lcUnits.join(', ');
  return (
    <div className={embedded ? undefined : 'lc-card'}>
      {!embedded && <div className="lc-h">{emoText(al('addLandedCost'))}</div>}
      {loadingOps && <div className="lc-note">{t('loading')}</div>}

      <div className="lc-form">
        <div className="wide">
          <Field label={`${t('op')} *`}>
            {preselectedOperationId ? (
              <div className="lc-fixed">{preselectedOperationId}</div>
            ) : (
              <EnumSelect bare value={selectedOpId} onChange={v => { setSelectedOpId(v); setError(null); }}>
                <option value="">— {t('opPh')} —</option>
                {operations.map(op => {
                  const items = [...new Set(op.lines.map(l => l.item_name).filter(Boolean))].join(', ');
                  return (
                    <option key={op.operation_id} value={op.operation_id}>
                      {t('opOpt', { id: op.operation_id, partner: op.partnerName || '—', items: items || '—', date: formatDate(op.date), n: op.lines.length })}
                    </option>
                  );
                })}
              </EnumSelect>
            )}
          </Field>
        </div>

        <Field label={`${t('amount')} *`}>
          <AmountInput data-testid="lc-amount" className={`avf-ctl${amount && !(parseFloat(amount) > 0) ? ' bad' : ''}`} min="0" value={amount}
            onChange={e => setAmount(e.target.value)} placeholder="0" />
        </Field>

        <Field label={t('method')} help={t('methodHelp')}>
          <Seg value={method} opts={[
            { k: 'by_quantity', id: 'lc-method-byqty', label: t('byQty'), disabled: byQtyDisabled, title: byQtyDisabled ? t('byQtyOff', { units: unitsTxt }) : undefined, onClick: () => { if (!byQtyDisabled) setMethod('by_quantity'); } },
            { k: 'by_value', id: 'lc-method-byvalue', label: t('byValue'), onClick: () => setMethod('by_value') },
          ]} />
        </Field>

        {/* LC service provider (supplier) — MANDATORY, exactly like New Purchase's partner. Use the Various-Supplier
            catch-all when unknown. On Credit the account locks to the partner's Financial Account (or shows the
            no-account notice); on Cash a cash account is picked. */}
        <Field label={`${t('partner')} *`}>
          <EnumSelect bare testid="lc-supplier" invalid={!lcSupplierId} value={lcSupplierId} onChange={val => { const v = val; setLcSupplierId(v); if (!v) setLcAccountFrom(''); }}>
            <option value="">— {t('partnerPh')} —</option>
            {lcSupplierOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </EnumSelect>
          {lcSupplierScope.kind === 'orphaned' && <div className="lc-msg">{lcSupplierScope.message}</div>}
        </Field>

        <Field label={t('payMethod')}>
          <Seg value={lcPayment} opts={[
            { k: 'Cash', id: 'lc-pay-cash', label: t('cash'), onClick: () => { setLcPayment('Cash'); setLcAccountFrom(''); } },
            { k: 'Credit', id: 'lc-pay-credit', label: t('credit'), onClick: () => { setLcPayment('Credit'); setLcAccountFrom(''); } },
          ]} />
        </Field>

        {lcPayment === 'Cash' ? (
          <Field label={`${t('payAcct')} *`}>
            <AccountPick accounts={cashBankAccounts} value={lcAccountFrom} onChange={setLcAccountFrom} placeholder={`— ${t('cashAcctPh')} —`} />
          </Field>
        ) : (
          <>
            <Field label={`${t('suppAcct')} *${lcCreditLocked ? ` (${t('auto')})` : ''}`}>
              <EnumSelect bare value={lcAccountFrom} disabled={lcCreditLocked} onChange={v => setLcAccountFrom(v)}>
                <option value="">— {t('suppAcctPh')} —</option>
                {/* Credit + supplier chosen → ONLY the resolved Financial Account (never "any supplier account"). */}
                {(lcSupplierId && lcResolvedAcct ? supplierAccounts.filter(a => a.id === lcResolvedAcct) : []).map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </EnumSelect>
              {lcSupplierId && !lcResolvedAcct && <div className="lc-msg">{t('noAcct', { name: selectedLcSupplier?.name || t('thisSupplier') })}</div>}
            </Field>
            <Field label={`${t('payDays')} *`}>
              <TextInput className={`avf-ctl${!lcPaymentDays ? ' bad' : ''}`} type="number" min="0" value={lcPaymentDays}
                onChange={e => setLcPaymentDays(e.target.value)} placeholder={t('payDaysPh')} />
            </Field>
          </>
        )}

        <Field label={`${t('date')} *`}>
          <TextInput className="avf-ctl" type="date" value={lcDate} onChange={e => setLcDate(e.target.value)} />
        </Field>

        <div className="wide">
          <Field label={t('notes')}>
            <textarea className="avf-ctl" style={{ height: 'auto', padding: '8px 10px', resize: 'vertical' }} rows={2} value={lcNotes}
              onChange={e => setLcNotes(e.target.value)} placeholder={t('notesPh')} />
          </Field>
        </div>
        <ReferenceField className="wide" id="lc-reference" value={lcReference} onChange={setLcReference} />
      </div>

      {/* Model A: LC posts to holding, allocated at receipt; with received qty the D2 catch-up block appears after preview. */}
      {!catchupPreview && <div className="lc-note">{t('holdingNote')}</div>}

      {/* 115 (B7): D2 late-LC catch-up dual-option UI — rendered from the preview payload (flags READ). */}
      {catchupPreview && (
        <div className="lc-note" style={{ background: 'var(--card)' }}>
          <div className="lc-opt-h" style={{ color: 'var(--forest)' }}>{t('cuTitle')}</div>
          <div className="lc-opt-b" style={{ marginBottom: 12 }}>{t('cuShare', { v: fmt(catchupPreview.received_share) })}</div>

          {(catchupPreview.pool || []).map((p, i) => {
            const disabled = !catchupPreview.pool_allowed;
            const selected = catchupChoice === 'pool';
            const sn = p.storage_name || t('storage');
            return (
              <div key={p.line_id + '::' + (p.storage_id || i)} className={`lc-opt${selected ? ' on' : ''}${disabled ? ' off' : ''}`}
                onClick={() => { if (!disabled) setCatchupChoice('pool'); }}>
                <div className="lc-opt-h">
                  <input type="radio" name="catchup" checked={selected} disabled={disabled} readOnly />
                  <span>{p.storage_name ? t('poolIn', { s: p.storage_name }) : t('pool')}</span>
                </div>
                <div className="lc-opt-b">{t('poolBody', { qty: fmt(p.on_hand_qty), s: sn, a: fmt(p.avg_cost_before), b: fmt(p.avg_cost_after), c: fmt(p.catchup_share) })}</div>
                {disabled && catchupPreview.pool_blocked_reason && <div className="lc-msg">{catchupPreview.pool_blocked_reason}</div>}
              </div>
            );
          })}

          {/* FUTURE option — absent when fully received (no future qty). */}
          {!catchupPreview.fully_received && catchupPreview.future && (
            <div className={`lc-opt${catchupChoice === 'future' ? ' on' : ''}`} onClick={() => setCatchupChoice('future')}>
              <div className="lc-opt-h"><input type="radio" name="catchup" checked={catchupChoice === 'future'} readOnly /><span>{t('future')}</span></div>
              <div className="lc-opt-b">{t('futureBody', { a: fmt(catchupPreview.future.exit_price_after), b: fmt(catchupPreview.future.exit_price_before), c: fmt(catchupPreview.future.understatement_amount) })}</div>
            </div>
          )}

          {/* Both-impossible fallback: mandatory acknowledgement above the Pool confirm. */}
          {bothImpossible && <div className="lc-note warn" style={{ marginTop: 4, marginBottom: 0 }}><CheckField checked={poolAck} onChange={setPoolAck} label={t('ack')} /></div>}
        </div>
      )}

      {error && <div className="lc-note crit" role="alert">{error}</div>}

      <div className="lc-foot">
        <button type="button" className="btn" onClick={onClose} disabled={submitting}>{lineText(al('cancel'))}</button>
        {!catchupPreview ? (
          <button type="button" className="btn success" data-testid="lc-submit" onClick={handleSubmit} disabled={submitting || loadingPreview}>
            {loadingPreview ? t('checking') : (submitting ? t('posting') : lineText(al('addLandedCost')))}
          </button>
        ) : (
          <button type="button" className="btn success" onClick={() => postLandedCost(catchupChoice)} disabled={submitDisabled}>
            {submitting ? t('posting') : confirmLbl}
          </button>
        )}
      </div>
    </div>
  );
}
